import { ArrowDown, ArrowUp, Check, Plus, Trash2, X } from 'lucide-react'
import type { LiveQuestion, LiveQuestionType } from '@/api/live'
import { Button } from '@/components/ui/button'

export const MAX_QUESTIONS = 20
const MAX_OPTIONS = 6

/** An editable question: the wire shape plus a client-only key so React can track rows through edits and reordering. */
export type QuestionDraft = LiveQuestion & { key: string }

export const newKey = () => crypto.randomUUID()
export const toQuestionDraft = (q: LiveQuestion): QuestionDraft => ({ ...q, correctIndexes: q.correctIndexes ?? null, explanation: q.explanation ?? null, key: newKey() })

const TRUE_FALSE = ['True', 'False']

export const blankQuestion = (type: LiveQuestionType): QuestionDraft => ({
  key: newKey(), id: null, type, text: '',
  options: type === 'choice' || type === 'multiple' ? ['', ''] : type === 'truefalse' ? [...TRUE_FALSE] : null,
  correctIndex: null,
  correctIndexes: null,
  explanation: null,
})

/** Questions whose answers the trainer writes. */
const hasOptions = (type: LiveQuestionType) => type === 'choice' || type === 'multiple'
/** Questions with answers to tick (and an explanation to give): the two above, and true/false, whose answers are fixed. */
const hasAnswerKey = (type: LiveQuestionType) => hasOptions(type) || type === 'truefalse'

/** The wire shape of a draft: trimmed, with what doesn't apply to its type dropped. */
export const fromQuestionDraft = ({ key: _key, ...q }: QuestionDraft): LiveQuestion => ({
  ...q,
  text: q.text.trim(),
  options: hasOptions(q.type) ? (q.options ?? []).map(o => o.trim()) : q.type === 'truefalse' ? [...TRUE_FALSE] : null,
  correctIndex: q.type === 'choice' || q.type === 'truefalse' ? q.correctIndex : null,
  correctIndexes: q.type === 'multiple' && q.correctIndexes && q.correctIndexes.length > 0 ? [...q.correctIndexes].sort((a, b) => a - b) : null,
  explanation: hasAnswerKey(q.type) && q.explanation?.trim() ? q.explanation.trim() : null,
})

const TYPE_LABEL: Record<LiveQuestionType, string> = {
  choice: 'Single choice',
  multiple: 'Multiple choice (several correct)',
  truefalse: 'True / false',
  rating: 'Rating 1–10',
  text: 'Open text',
}

/** The option numbers marked correct, whichever kind of question this is. */
const correctOf = (q: QuestionDraft): number[] =>
  q.type === 'choice' || q.type === 'truefalse' ? (q.correctIndex === null ? [] : [q.correctIndex]) : q.type === 'multiple' ? (q.correctIndexes ?? []) : []

/** `needsAnswer` is for a fastest-finger game, where a question without a right answer can't be raced. */
export function questionProblem(q: QuestionDraft, needsAnswer = false): string | null {
  if (!q.text.trim()) return 'Add the question text.'
  if (hasOptions(q.type)) {
    const filled = (q.options ?? []).filter(o => o.trim())
    if (filled.length < 2) return 'Add at least two options.'
    if ((q.options ?? []).some(o => !o.trim())) return 'Fill in or remove the empty option.'
  }
  if (needsAnswer && hasAnswerKey(q.type) && correctOf(q).length === 0) return 'Tick the one right answer.'
  return null
}

/** Shows whether the question will be scored, and offers the way back to "not scored". */
function ScoreStatus({ q, onClear, needsAnswer }: { q: QuestionDraft; onClear: () => void; needsAnswer: boolean }) {
  const marked = correctOf(q).length
  if (marked === 0 && needsAnswer) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800">
        Needs a right answer
        <span className="font-normal text-amber-700">· a race needs one</span>
      </span>
    )
  }
  if (marked === 0) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
        Not scored
        <span className="font-normal text-gray-500">· no answer is marked correct</span>
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-2">
      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-800">
        <Check size={12} strokeWidth={3} /> Scored
      </span>
      <button
        type="button"
        onClick={onClear}
        className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-600 hover:border-red-200 hover:bg-red-50 hover:text-red-700 cursor-pointer"
      >
        <X size={12} /> {marked > 1 ? 'Clear correct answers' : 'Clear correct answer'}
      </button>
    </span>
  )
}

function QuestionRow({
  q, index, count, ratingOnly, raceMode, onChange, onMove, onDelete,
}: {
  q: QuestionDraft; index: number; count: number; ratingOnly: boolean; raceMode: boolean
  onChange: (next: QuestionDraft) => void; onMove: (dir: -1 | 1) => void; onDelete: () => void
}) {
  const trueFalse = q.type === 'truefalse'
  const options = trueFalse ? TRUE_FALSE : (q.options ?? [])
  const multiple = q.type === 'multiple'
  const correct = correctOf(q)
  const problem = questionProblem(q, raceMode)

  const setType = (type: LiveQuestionType) => {
    if (type === q.type) return
    if (type === 'truefalse') {
      // Only a single choice's answer can carry over, and only if it was the first or second option.
      onChange({ ...q, type, options: [...TRUE_FALSE], correctIndex: q.type === 'choice' && (q.correctIndex === 0 || q.correctIndex === 1) ? q.correctIndex : null, correctIndexes: null })
      return
    }
    if (q.type === 'truefalse' && hasOptions(type)) {
      onChange({ ...q, type, options: [...TRUE_FALSE], correctIndex: type === 'choice' ? q.correctIndex : null, correctIndexes: type === 'multiple' && q.correctIndex !== null ? [q.correctIndex] : null })
      return
    }
    // Keep the options (and whichever answer is marked) when switching between single and multiple choice.
    if (hasOptions(type) && hasOptions(q.type)) {
      onChange({
        ...q, type,
        correctIndex: type === 'choice' ? (correct[0] ?? null) : null,
        correctIndexes: type === 'multiple' && correct.length > 0 ? correct : null,
      })
      return
    }
    onChange({ ...q, type, options: hasOptions(type) ? (q.options ?? ['', '']) : null, correctIndex: null, correctIndexes: null })
  }

  const clearCorrect = () => onChange({ ...q, correctIndex: null, correctIndexes: null })

  /** Tick or untick an option. A single choice has one right answer, so ticking another moves it; ticking it again clears it. */
  const toggleCorrect = (i: number) => {
    if (multiple) {
      const next = correct.includes(i) ? correct.filter(x => x !== i) : [...correct, i].sort((a, b) => a - b)
      onChange({ ...q, correctIndexes: next.length > 0 ? next : null })
    } else {
      onChange({ ...q, correctIndex: q.correctIndex === i ? null : i })
    }
  }

  const removeOption = (i: number) => {
    const reindexed = correct.filter(x => x !== i).map(x => (x > i ? x - 1 : x))
    onChange({
      ...q,
      options: options.filter((_, j) => j !== i),
      correctIndex: q.type === 'choice' ? (reindexed[0] ?? null) : null,
      correctIndexes: multiple && reindexed.length > 0 ? reindexed : null,
    })
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-3">
      <div className="flex items-start gap-2">
        <span className="mt-2 text-xs font-semibold text-gray-400 w-5 shrink-0">{index + 1}.</span>
        <div className="flex-1 min-w-0 space-y-2">
          <input
            value={q.text}
            maxLength={300}
            placeholder={ratingOnly ? 'Statement, e.g. Using an AED' : 'Question'}
            aria-label={`Question ${index + 1}`}
            onChange={e => onChange({ ...q, text: e.target.value })}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          {!ratingOnly && (
            <select
              value={q.type}
              onChange={e => setType(e.target.value as LiveQuestionType)}
              aria-label="Question type"
              className="rounded-md border border-gray-300 px-2 py-1.5 text-xs text-gray-700 bg-white"
            >
              {(Object.keys(TYPE_LABEL) as LiveQuestionType[]).filter(t => !raceMode || t === 'choice' || t === 'truefalse').map(t => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
            </select>
          )}
        </div>
        <div className="flex flex-col gap-0.5 shrink-0">
          <button type="button" title="Move up" disabled={index === 0} onClick={() => onMove(-1)} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30 cursor-pointer"><ArrowUp size={14} /></button>
          <button type="button" title="Move down" disabled={index === count - 1} onClick={() => onMove(1)} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30 cursor-pointer"><ArrowDown size={14} /></button>
        </div>
        <button type="button" title="Delete question" onClick={onDelete} className="p-1 mt-1.5 text-gray-400 hover:text-red-600 cursor-pointer shrink-0"><Trash2 size={15} /></button>
      </div>

      {hasAnswerKey(q.type) && (
        <div className="pl-7 space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
            <p className="text-xs font-medium text-gray-700">
              Answers: <span className="font-normal text-gray-500">{multiple ? 'tick every correct one' : 'tick the correct one'}</span>
            </p>
            <ScoreStatus q={q} onClear={clearCorrect} needsAnswer={raceMode} />
          </div>

          <div className="space-y-1.5" role={multiple ? 'group' : 'radiogroup'} aria-label={multiple ? 'Answers, tick every correct one' : 'Answers, tick the correct one'}>
            {options.map((o, i) => {
              const isCorrect = correct.includes(i)
              return (
                <div
                  key={i}
                  className={`flex items-center gap-2 rounded-lg border px-2 py-1.5 transition-colors ${isCorrect ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-white'}`}
                >
                  <button
                    type="button"
                    role={multiple ? 'checkbox' : 'radio'}
                    aria-checked={isCorrect}
                    aria-label={isCorrect ? `Option ${i + 1} is correct. Click to unmark` : `Mark option ${i + 1} as correct`}
                    title={isCorrect ? 'Marked correct. Click to unmark' : 'Mark as correct'}
                    onClick={() => toggleCorrect(i)}
                    className={`h-7 w-7 shrink-0 flex items-center justify-center border-2 cursor-pointer transition-colors ${multiple ? 'rounded-md' : 'rounded-full'} ${
                      isCorrect
                        ? 'bg-green-600 border-green-600 text-white'
                        : 'border-gray-300 bg-white text-transparent hover:border-green-500 hover:text-green-500'
                    }`}
                  >
                    <Check size={15} strokeWidth={3} />
                  </button>
                  {trueFalse ? (
                    <span className="flex-1 min-w-0 px-1 text-sm font-medium text-gray-800">{o}</span>
                  ) : (
                    <input
                      value={o}
                      maxLength={80}
                      placeholder={`Option ${i + 1}`}
                      aria-label={`Option ${i + 1}`}
                      onChange={e => onChange({ ...q, options: options.map((x, j) => (j === i ? e.target.value : x)) })}
                      className="flex-1 min-w-0 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  )}
                  {isCorrect && <span className="shrink-0 text-xs font-semibold text-green-700">Correct</span>}
                  {!trueFalse && options.length > 2 && (
                    <button type="button" title="Remove option" aria-label={`Remove option ${i + 1}`} className="text-gray-400 hover:text-red-600 cursor-pointer" onClick={() => removeOption(i)}>
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              )
            })}
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            {!trueFalse && options.length < MAX_OPTIONS && (
              <button type="button" onClick={() => onChange({ ...q, options: [...options, ''] })} className="text-xs text-indigo-600 hover:text-indigo-800 cursor-pointer">+ Add option</button>
            )}
            <p className="text-xs text-gray-400">
              {correct.length === 0
                ? (raceMode ? 'Tick the one right answer. The quickest person to pick it wins the question.' : 'Tick an answer to score this question, or leave it blank for an opinion question.')
                : multiple
                  ? 'A person gets this right only if they pick exactly the correct options.'
                  : 'A person gets this right if they pick the correct answer.'}
            </p>
          </div>

          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-700" htmlFor={`explanation-${q.key}`}>
              Explanation <span className="font-normal text-gray-500">(optional, shown when you reveal the answer)</span>
            </label>
            <textarea
              id={`explanation-${q.key}`}
              value={q.explanation ?? ''}
              rows={2}
              maxLength={500}
              placeholder="Why it's right, e.g. Cooling a burn under running water for 20 minutes limits the damage."
              onChange={e => onChange({ ...q, explanation: e.target.value })}
              className="w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>
      )}

      {q.type === 'rating' && <p className="pl-7 text-xs text-gray-400">{ratingOnly ? 'Attendees rate their confidence from 1 (not at all) to 10 (very).' : 'Attendees pick a number from 1 to 10.'}</p>}
      {q.type === 'text' && <p className="pl-7 text-xs text-gray-400">Attendees type a short, optional comment.</p>}
      {problem && <p className="pl-7 text-xs text-amber-700">{problem}</p>}
    </div>
  )
}

/** An ordered, editable list of questions. `ratingOnly` is for confidence checks: every question is a 1–10 rating. */
export function QuestionListEditor({
  questions, onChange, ratingOnly = false, raceMode = false,
}: {
  questions: QuestionDraft[]
  onChange: (next: QuestionDraft[]) => void
  ratingOnly?: boolean
  /** A fastest-finger game: single choice and true/false only, and every question needs its right answer. */
  raceMode?: boolean
}) {
  const update = (i: number, next: QuestionDraft) => onChange(questions.map((q, j) => (j === i ? next : q)))
  const move = (i: number, dir: -1 | 1) => {
    const next = [...questions]; const j = i + dir
    if (j < 0 || j >= next.length) return
    ;[next[i], next[j]] = [next[j], next[i]]
    onChange(next)
  }

  return (
    <div className="space-y-3">
      {questions.map((q, i) => (
        <QuestionRow
          key={q.key} q={q} index={i} count={questions.length} ratingOnly={ratingOnly} raceMode={raceMode}
          onChange={next => update(i, next)} onMove={dir => move(i, dir)}
          onDelete={() => onChange(questions.filter((_, j) => j !== i))}
        />
      ))}
      {questions.length < MAX_QUESTIONS && (
        <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => onChange([...questions, blankQuestion(ratingOnly ? 'rating' : 'choice')])}>
          <Plus size={14} /> {ratingOnly ? 'Add statement' : 'Add question'}
        </Button>
      )}
    </div>
  )
}
